<template>
    <div>

        <PageHeader :title="$t('generateBuyBoxWidget')" :subtitle="$t('generateBuyBoxWidgetDescription')">
            <template #bottom>
                <div class="d-flex flex-wrap gap-3">
                    <ButtonIcon
                        variant="link"
                        class="p-0"
                        icon="file-text"
                        :icon-size="24"
                        @click="openHelp"
                    >
                        {{ $t('takeALookAtHelp') }}
                    </ButtonIcon>
                    <ButtonIcon
                        href="https://getbuybox.com/wygeneruj-widzet-produktowy-buy-box-i-wstaw-na-swoja-strone-www/"
                        target="_blank"
                        variant="link"
                        class="p-0"
                        icon="youtube"
                        :icon-size="24"
                    >
                        {{ $t('watchVideo') }}
                    </ButtonIcon>
                </div>
            </template>
        </PageHeader>

        <div class="d-flex gap-4">

            <div class="d-flx flex-column flex-grow-1 flex-basis-0">

                <Card :header="`1. ${$t('selectProduct')}`" header-id="WTBG-header-1" :state="step1State">
                    <ProductSearch id="form-product" v-model="product" class="product-search" />
                </Card>

                <Card :header="`2. ${$t('choosePageAndConfiguration')}`" header-id="WTBG-header-2" :state="step2State">
                    <div v-show="productId" class="d-flex flex-column gap-3">
                        <Multiselect
                            id="form-space"
                            v-model="space"
                            :initial-value="Number.parseInt($route.query.space) || undefined"
                            :fetch-options-function="fetchSpaces"
                            searchable
                            track-by="id"
                            label="name"
                            select-label=""
                            :placeholder="$t('selectWebsite')"
                        />
                        <Multiselect
                            v-if="spaceId"
                            id="form-space-id"
                            ref="buyboxField"
                            :key="`${productId}-${spaceId}`"
                            v-model="buybox"
                            :initial-value="Number.parseInt($route.query.buybox) || undefined"
                            :fetch-options-function="fetchBuyboxes"
                            searchable
                            track-by="id"
                            label=""
                            :custom-label="formatBuyboxName"
                            select-label=""
                            :placeholder="$t('chooseConfiguration')"
                            @loading="loading => buyboxLoading = loading"
                        />
                    </div>
                </Card>

                <Card :sub-header="showOptions ? $t('matchTheAppearanceOfTheWidgetSubheader') : ''" header-id="WTBG-header-3" :state="step3State" :class="{ 'collapsed': !showOptions }">
                    <template #header>
                        <div class="d-flex align-items-center">
                            <span v-if="showOptions">3. {{ $t('matchTheAppearanceOfTheWidget') }}</span>
                            <ButtonIcon
                                variant="link"
                                class="options-header-text expandable p-0"
                                :class="{ 'collapsed': !showOptions }"
                                icon="chevron-up"
                                icon-class="text-primary"
                                flip-icon
                                :disabled="step3State === 'disabled'"
                                @click="showOptions = !showOptions"
                            >
                                <span v-if="!showOptions" id="WTBG-header-3" class="text-link-header text-primary">3. {{ $t('matchTheAppearanceOfTheWidgetOptional') }}</span>
                                <span v-else id="WTBG-header-3" class="text-link-header ml-3">{{ $t('collapse') }}</span>
                            </ButtonIcon>
                        </div>
                    </template>
                    <div v-show="showOptions">
                        <b-form-group v-if="isVersion6" id="form-group-type" :label="$t('typeOfWidget')" label-cols-sm="3">
                            <b-form-select id="form-type" v-model="options.type" :options="optionsType" />
                        </b-form-group>
                        <b-form-group v-if="isVersion6" id="form-group-dark-mode" :label="$t('darkModePreference')" label-cols-sm="3">
                            <b-form-select id="form-dark-mode" v-model="options.darkMode" :options="optionsDarkMode" />
                        </b-form-group>
                        <b-form-group v-if="!isVersion6" id="form-group-html-tag" :label="$t('htmlTag')" label-cols-sm="3">
                            <b-form-select id="form-html-tag" v-model="options.htmlTag" :options="optionsHtmlTag" />
                        </b-form-group>
                        <b-form-group id="form-group-language" :label="$t('languageOfWidget')" label-cols-sm="3">
                            <b-form-select id="form-language" v-model="options.language" :options="optionsLanguage" />
                        </b-form-group>
                        <b-form-group v-if="!isVersion6 || options.type !== 'logos-only'" id="form-group-product-name" :label="$t('productName')" label-cols-sm="3">
                            <b-form-input id="form-product-name" v-model="options.productName" />
                        </b-form-group>
                        <b-form-group v-if="!isVersion6 || options.type !== 'logos-only'" id="form-group-product-info" :label="$t('productDescription')" label-cols-sm="3">
                            <b-form-input id="form-product-info" v-model="options.productInfo" />
                        </b-form-group>
                        <b-form-group id="form-group-lead-color" :label="$t('leadColor')" label-cols-sm="3">
                            <ColorField id="form-lead-color" v-model="options.leadColor" :validation="$v.options.leadColor" :placeholder="$t('textLinks.optional')" class="m-0" />
                        </b-form-group>
                        <b-form-group v-if="!isVersion6 || options.type !== 'logos-only'" id="form-group-button-label" :label="$t('customButtonContent')" label-cols-sm="3">
                            <b-form-input id="form-button-label" v-model.trim="options.buttonLabel" :maxlength="15" />
                        </b-form-group>
                        <b-form-group v-if="!isVersion6 || options.type !== 'logos-only'" id="form-group-shop-style" :label="$t('shopPresentation')" label-cols-sm="3">
                            <b-form-select id="form-shop-style" v-model="options.shopStyle" :options="optionsShopStyle" />
                        </b-form-group>
                        <b-form-group id="form-group-sort-type" :label="$t('orderOfOffers')" label-cols-sm="3">
                            <b-form-select id="form-sort-type" v-model.number="options.sortType" :options="optionsSortType" />
                        </b-form-group>
                        <b-form-group v-if="!isVersion6 || options.type === 'widget'" id="form-group-row-count" :label="$t('offersNumber')" label-cols-sm="3">
                            <b-form-input id="form-row-count" v-model.number="options.rowCount" type="number" />
                        </b-form-group>
                        <b-form-group id="form-group-show-product" :label="$t('showProductDetails')" label-cols-sm="3">
                            <div class="d-flex align-items-center h-100">
                                <b-form-checkbox id="form-show-product" v-model="options.showProduct" switch />
                            </div>
                        </b-form-group>
                        <b-form-group v-if="!isVersion6 || options.type !== 'logos-only'" id="form-group-show-prices" :label="$t('showPrices')" label-cols-sm="3">
                            <div class="d-flex align-items-center h-100">
                                <b-form-checkbox id="form-show-prices" v-model="options.showPrices" switch />
                            </div>
                        </b-form-group>
                        <b-form-group v-if="!isVersion6 || options.type !== 'logos-only'" id="form-group-show-availability" :label="$t('showAvailability')" label-cols-sm="3">
                            <div class="d-flex align-items-center h-100">
                                <b-form-checkbox id="form-show-availability" v-model="options.showAvailability" switch />
                            </div>
                        </b-form-group>
                    </div>
                </Card>

                <Card :header="`4. ${$t('putOnThePage')}`" header-id="WTBG-header-4" :sub-header="$t('putOnThePageSubheader')" :state="step4State">
                    <pre class="widget-code">{{ widgetCode }}</pre>
                    <div class="my-3">
                        <ButtonIcon
                            variant="link"
                            class="expandable p-0"
                            :class="{ 'collapsed': !showParameters }"
                            icon="chevron-up"
                            flip-icon
                            @click="showParameters = !showParameters"
                        >
                            {{ $t('addYourOwnParameters') }}
                        </ButtonIcon>
                        <b-collapse v-model="showParameters">
                            <div class="parameters">
                                <div class="my-2"></div>
                                <b-form-group id="form-group-abpar1" :label="$t('abpar1')" label-cols-sm="3">
                                    <b-form-input id="form-abpar1" v-model="options.abpar1" />
                                </b-form-group>
                                <b-form-group id="form-group-abpar2" :label="$t('abpar2')" label-cols-sm="3">
                                    <b-form-input id="form-abpar2" v-model="options.abpar2" />
                                </b-form-group>
                                <b-form-group id="form-group-abpar3" :label="$t('abpar3')" label-cols-sm="3">
                                    <b-form-input id="form-abpar3" v-model="options.abpar3" />
                                </b-form-group>
                            </div>
                        </b-collapse>
                    </div>
                    <ButtonIcon variant="success" icon="copy" @click="copyWidgetCode()">{{ $t('copyCode') }}</ButtonIcon>
                </Card>

            </div>

            <div class="d-none d-md-flex flex-column flex-grow-1 flex-basis-0 widget-preview-wrapper-container">

                <Card :header="$t('preview')" header-id="WTBG-header-5" class="widget-preview-wrapper">

                    <div v-if="step2State === 'completed'" class="d-flex gap-3 flex-column">
                        <div class="d-flex gap-2 justify-content-center">
                            <ButtonIcon variant="light" size="lg" class="btn-preview-icon p-2" :pressed="previewMode === 'desktop'" @click="previewMode = 'desktop'">
                                <template #icon>
                                    <img src="@/assets/images/computer.png" alt="" width="24px" class="">
                                </template>
                            </ButtonIcon>
                            <ButtonIcon variant="light" size="lg" class="btn-preview-icon p-2" :pressed="previewMode === 'mobile'" @click="previewMode = 'mobile'">
                                <template #icon>
                                    <img src="@/assets/images/smartphone.png" alt="" width="24px" class="">
                                </template>
                            </ButtonIcon>
                        </div>
                        <div class="d-flex justify-content-center">
                            <div :class="`widget-preview-mode-${previewMode}`" class="w-100 d-flex flex-column justify-content-center align-items-center flex-grow-1 text-center height-min-100">
                                <IframeWidgetPreview
                                    v-bind="widgetPreviewData"
                                    :reload-delay="100"
                                    @BBLoading="widgetLoading = true"
                                    @NoData="widgetLoading = false"
                                    @BBLoaded="widgetLoading = false"
                                />
                                <b-spinner v-if="widgetLoading" />
                            </div>
                        </div>
                    </div>

                    <div v-else class="bg-soft-secondary d-flex justify-content-center align-items-center rounded height-min-300 font-size-15 text-center">
                        <div class="width-max-300">{{ $t('chooseAProductPageAndConfigurationToSee') }}</div>
                    </div>
                </Card>

            </div>

        </div>

    </div>
</template>



<script>
    import cloneDeep from 'lodash/cloneDeep';
    import pick from 'lodash/pick';
    import camelCase from 'camelcase';
    import ColorField from '@/components/common/ColorField.vue';
    import PageHeader from '@/components/common/PageHeader.vue';
    import ProductSearch from '@/components/common/ProductSearch.vue';
    import Multiselect from '@/components/common/Multiselect.vue';
    import IframeWidgetPreview from '@/components/common/IframeWidgetPreview.vue';
    import spaceService from '@/services/spaceService';
    import pluginService, { SortTypeToWidgetSort } from '@/services/pluginService';
    import { getLanguageOptions } from '@/i18n/i18n';
    import { generateId } from '@/utils/util';
    import { hexColor } from '@/utils/validators';

    const DEFAULT_OPTIONS = Object.freeze({
        type: 'widget',
        darkMode: null,
        htmlTag: 'div',
        language: null,
        productName: null,
        productInfo: null,
        leadColor: null,
        buttonLabel: null,
        shopStyle: null,
        sortType: null,
        rowCount: null,
        showProduct: false,
        showPrices: false,
        showAvailability: false,
        abpar1: null,
        abpar2: null,
        abpar3: null,
    });

    export default {
        name: 'WidgetGenerator',
        components: {
            PageHeader,
            ProductSearch,
            Multiselect,
            ColorField,
            IframeWidgetPreview,
        },
        validations: {
            options: {
                leadColor: {
                    hexColor: value => (!value ? true : hexColor(value)),
                },
            },
        },
        data() {
            return {
                widgetScriptUrl: process.env.VUE_APP_WIDGET_SCRIPT_URL,
                product: null,
                space: null,
                buybox: null,
                buyboxLoading: false,
                showOptions: false,
                showParameters: false,
                options: { ...DEFAULT_OPTIONS },
                widgetId: generateId(),
                previewMode: 'desktop',
                widgetLoading: false,
            };
        },
        computed: {
            productId() {
                return this.product?.id || null;
            },
            offerId() {
                return this.product?.offerId || null;
            },
            spaceId() {
                return this.space?.id || null;
            },
            buyboxId() {
                return this.buybox?.id || null;
            },
            buyboxes() {
                if (!this.spaceId) return [];
                if (this.buyboxLoading) return []; // The `buyboxLoading` variable is necessary to trigger this computed property, allowing it to access the value of `loadedOptions` from `$refs.buyboxField`.
                return this.$refs.buyboxField?.loadedOptions || [];
            },
            hasConfiguration() {
                return !!this.buyboxes.length;
            },
            step1State() {
                return this.productId ? 'completed' : 'default';
            },
            step2State() {
                if (!this.productId) return 'disabled';
                if (!this.hasConfiguration && this.buyboxLoading) return 'default';
                if (!this.hasConfiguration) return this.spaceId ? 'completed' : 'default';
                if (this.hasConfiguration) return this.buyboxId ? 'completed' : 'default';
                return 'default';
            },
            step3State() {
                if (this.step2State === 'completed') return 'default';
                return 'disabled';
            },
            step4State() {
                return this.step3State;
            },
            optionsType() {
                if (this.isVersion6) {
                    return [
                        { value: 'widget', text: this.$t('widgetType.widget'), htmlTag: 'div' },
                        { value: 'offcanvas', text: this.$t('widgetType.offCanvas'), htmlTag: 'button' },
                        { value: 'offcanvas-link', text: this.$t('widgetType.offCanvasLink'), htmlTag: 'a' },
                        { value: 'logos-only', text: this.$t('widgetType.logosOnly'), htmlTag: 'div' },
                    ];
                }
                return [
                    { value: 'widget', text: this.$t('widgetType.widget'), htmlTag: 'div' },
                    { value: 'popover', text: this.$t('widgetType.popover'), htmlTag: 'button' },
                    { value: 'overlay', text: this.$t('widgetType.overlay'), htmlTag: 'a' },
                    { value: 'logos-only', text: this.$t('widgetType.logosOnly'), htmlTag: 'div' },
                ];
            },
            optionsLanguage() {
                return getLanguageOptions(true);
            },
            optionsHtmlTag() {
                return [
                    { value: 'div', text: 'div' },
                    { value: 'span', text: 'span' },
                    { value: 'a', text: 'a' },
                    { value: 'button', text: 'button' },
                ];
            },
            optionsShopStyle() {
                return this.$store.getters['options/shopStyleOptions'];
            },
            optionsSortType() {
                return this.$store.state.plugin.sortTypesIds.map(sortTypeId => ({
                    value: this.$store.state.plugin.sortTypes[sortTypeId].value,
                    text: this.$store.state.plugin.sortTypes[sortTypeId].label,
                }));
            },
            currentHtmlTag() {
                if (this.isVersion6) {
                    const typeOption = this.optionsType.find(opt => opt.value === this.options.type);
                    return typeOption?.htmlTag || 'div';
                }
                return this.options.htmlTag || 'div';
            },
            isVersion6() {
                return this.buybox?.version === 'latest' || this.buybox?.version === '6.0';
            },
            optionsDarkMode() {
                return [
                    { value: null, text: this.$t('default') },
                    { value: 'light', text: this.$t('widgetDarkMode.light') },
                    { value: 'dark', text: this.$t('widgetDarkMode.dark') },
                    { value: 'browser', text: this.$t('widgetDarkMode.browser') },
                ];
            },
            widgetData() {
                const attrs = {};

                /* eslint-disable */
                if (!this.buyboxId && this.spaceId)                                                         attrs['bb-space-id'] = this.spaceId;
                if (!(!this.buyboxId && this.spaceId))                                                      attrs['data-bb-id'] = this.buyboxId;
                if (this.offerId)                                                                           attrs['data-bb-oid'] = this.offerId;
                // For v6: logos-only uses data-bb-logos-only, offcanvas uses data-bb-type="offcanvas"
                if (this.isVersion6 && this.options.type === 'logos-only')                                  attrs['data-bb-logos-only'] = 'true';
                else if (this.isVersion6 && this.options.type === 'offcanvas')                              attrs['data-bb-type'] = 'offcanvas';
                else if (this.isVersion6 && this.options.type === 'offcanvas-link')                         attrs['data-bb-type'] = 'offcanvas-link';
                else if (!this.isVersion6 && this.options.type && this.options.type !== 'widget')           attrs['data-bb-type'] = this.options.type;
                if (!this.isVersion6 && this.options.type && this.options.type === 'popover')               attrs['data-bb-popover-event'] = 'click';
                if (this.options.language && this.options.language !== this.buybox?.language)               attrs['data-bb-language'] = this.options.language;
                if (this.options.shopStyle && this.options.shopStyle !== this.buybox?.shopStyle)            attrs['data-bb-shop-style'] = this.options.shopStyle;
                if (this.options.sortType && this.options.sortType !== this.buybox?.sortType)               attrs['data-bb-sort'] = SortTypeToWidgetSort[this.options.sortType];
                if (hexColor(this.options.leadColor) && this.options.leadColor !== this.buybox?.leadColor)  attrs['data-bb-lead-color'] = this.options.leadColor;
                if (this.options.buttonLabel && this.options.buttonLabel !== this.buybox?.buttonLabel)      attrs['data-bb-button-label'] = this.options.buttonLabel;
                if (this.options.rowCount && this.options.rowCount !== this.buybox?.rowCount)               attrs['data-bb-row-count'] = this.options.rowCount;
                if (this.options.showProduct !== this.buybox?.showProduct)                                  attrs['data-bb-show-extra'] = this.options.showProduct;
                if (this.options.showPrices !== this.buybox?.showPrices)                                    attrs['data-bb-show-prices'] = this.options.showPrices;
                if (this.options.showAvailability !== this.buybox?.showAvailability)                        attrs['data-bb-show-availability'] = this.options.showAvailability;
                if (this.options.productName)                                                               attrs['data-bb-name'] = this.options.productName;
                if (this.options.productInfo)                                                               attrs['data-bb-info'] = this.options.productInfo;
                if (this.isVersion6 && this.options.darkMode)                                               attrs['data-bb-dark-mode'] = this.options.darkMode;
                if (this.options.abpar1)                                                                    attrs['data-bb-p1'] = this.options.abpar1;
                if (this.options.abpar2)                                                                    attrs['data-bb-p2'] = this.options.abpar2;
                if (this.options.abpar3)                                                                    attrs['data-bb-p3'] = this.options.abpar3;
                /* eslint-enable */

                return attrs;
            },
            widgetCode() {
                const attrs = {
                    class: 'bb-widget',
                    id: `buybox-${this.widgetId}`,
                    ...this.widgetData,
                };
                const attrsStr = Object.keys(attrs).reduce((str, name) => `${str} ${name}="${attrs[name]}"`, '');
                const bbType = this.options.type === 'widget' ? undefined : this.options.type;
                const triggerBtn = ['popover', 'overlay'].includes(bbType) ? `<span style="text-decoration: underline; color: blue; cursor: pointer;">${this.$t('widgetPreview.show')}</span>` : '';
                // eslint-disable-next-line no-useless-escape
                return `<script src="${this.widgetScriptUrl}"><\/script>\n<${this.currentHtmlTag}${attrsStr}>${triggerBtn}</${this.currentHtmlTag}>`;
            },
            widgetPreviewData() {
                return Object.keys(this.widgetData).reduce((attrs, attrString) => {
                    const attrName = camelCase(attrString.replace('data-', ''));
                    return { ...attrs, [attrName]: this.widgetData[attrString] };
                }, {});
            },
        },
        watch: {
            product() {
                this.widgetId = generateId();
                this.space = null;
            },
            space() {
                this.buybox = null;
            },
            buybox() {
                this.resetOptions();
                if (this.buybox) this.initOptions();
            },
            step2State() {
                if (this.step2State !== 'completed') {
                    this.showOptions = false;
                    this.showParameters = false;
                }
            },
        },
        methods: {
            openHelp() {
                window.Gleap.openHelpCenterArticle('16', false);
            },
            fetchSpaces() {
                const userId = this.$store.getters['auth/getUser'].id;
                return spaceService.fetchSpaces(userId, { statuses: '1,2' }, false).then(data => data?.items || []);
            },
            fetchBuyboxes() {
                return pluginService.matchProductBuyboxes(this.spaceId, this.productId).then(items => items.map(item => ({ ...item.buybox, activeNo: item.activeNo, inactiveNo: item.inactiveNo })));
            },
            formatBuyboxName(option) {
                if (!option) return '';
                if (option.isInitial) return option.id;
                if (typeof option === 'number' || typeof option === 'string') return option;
                const textPositive = `${this.$t('numberOfOffersToDisplay').toLowerCase()}: ${option.activeNo}`;
                const textNegative = this.$t('noOffersToDisplay').toLowerCase();
                const text = option.activeNo ? textPositive : textNegative;
                return `${option.name} (${text})`;
            },
            async copyWidgetCode() {
                if (this.widgetCode) {
                    await this.$copyText(this.widgetCode);
                    this.$toastr.success(this.$t('widgetCodeCopiedSuccessful'));
                }
            },
            fetchSortTypes() {
                this.$store.dispatch('plugin/fetchSortTypes');
            },
            resetOptions() {
                const baseOptions = { ...DEFAULT_OPTIONS };
                // For non-v6 versions, reset type to undefined but keep htmlTag
                if (!this.isVersion6) {
                    baseOptions.type = undefined;
                    baseOptions.darkMode = undefined;
                } else {
                    baseOptions.htmlTag = undefined;
                }
                this.options = baseOptions;
            },
            initOptions() {
                if (!this.buybox) return;
                const buyboxClone = cloneDeep(this.buybox);
                const buyboxPick = pick(buyboxClone, Object.keys(this.options));
                // For non-v6 versions, don't initialize type and darkMode
                if (!this.isVersion6) {
                    delete buyboxPick.type;
                    delete buyboxPick.darkMode;
                } else {
                    delete buyboxPick.htmlTag;
                }
                this.options = { ...this.options, ...buyboxPick };
            },
        },
        created() {
            this.fetchSortTypes();
        },
    };
</script>



<style lang="scss" scoped>
    .product-search {
        display: flex;
        flex-direction: column-reverse;

        ::v-deep {
            .product-box {
                display: flex;
                align-items: flex-start;
            }
        }

        &.touched {
            ::v-deep {
                .selected-product {
                    display: none;
                }
            }
        }
    }

    .widget-code {
        border: 1px solid $pre-color;
        border-radius: $border-radius;
        white-space: wrap;
        font-size: 90%;
        color: #6c757d;
        line-height: 150%;
        padding: 16px;
        white-space: preserve;
    }

    ::v-deep {
        .card {
            transition: all 0.15s ease;

            &.collapsed {
                background: transparent;
                box-shadow: none;

                .card-body {
                    padding-top: 0;
                    padding-bottom: 0;

                    .header-title {
                        .options-header-text {
                            font-size: $font-size-base;
                            font-weight: normal;
                            color: $body-color;
                            opacity: 1;
                        }
                    }
                }
            }
        }
    }

    .parameters {
        display: flex;
        flex-direction: column;
    }

    .btn-preview-icon {
        &:active,
        &:focus {
            box-shadow: none !important;
        }
    }

    .widget-preview-mode-desktop {
        width: 760px;
        min-width: 760px;
    }

    .widget-preview-mode-mobile {
        width: 400px;
        min-width: 400px;
        max-width: 400px;
    }

    .widget-preview-wrapper-container {
        position: sticky;
        top: $topbar-height + $grid-gutter-width;
        z-index: 2;
        margin-bottom: auto;
    }
</style>
